/**
 * Unified Shift & Absences page with tabs
 * Combines shift planning and absence management
 * Owns shared filter/date state so it persists across tabs
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { Trans } from "@lingui/react/macro";
import { Clock, UserX, BarChart3 } from "lucide-react";
import { useSearchParams } from "react-router";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { ScrollableTabsList } from "@/components/ui/scrollable-tabs-list";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { useUrlTab } from "@/hooks/useUrlTab";

// Import the existing components
import { ShiftPlanningPage } from "./shifts";
import { AbsenceManagementPage } from "./shifts/AbsenceManagementPage";
import { CoverageDashboardPage } from "./shifts/CoverageDashboardPage";
import type { CalendarViewMode } from "@/types/shifts";

const TABS = ["coverage", "shifts", "absences"] as const;
type TabValue = (typeof TABS)[number];

const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses a strict YYYY-MM-DD URL parameter as a local calendar date.
 * Returns null when the value is missing, malformed, or not a real date.
 */
function parseLocalDateParam(value: string | null): Date | null {
  if (!value || !DATE_PARAM_PATTERN.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d
  ) {
    return null;
  }
  return date;
}

export default function ShiftAbsencesPage() {
  const [activeTab, setActiveTab] = useUrlTab<TabValue>("tab", TABS, "shifts");
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get("date");

  // Shared filter state — persists across Coverage ↔ Planning tabs
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedChildcareId, setSelectedChildcareId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);

  // Deep link from a split alert item (#1594): ?date=YYYY-MM-DD, parsed as a
  // LOCAL calendar date. A malformed or impossible date is ignored. Keyed on the
  // value alone so a tab switch (which rewrites the URL) keeps the picked date.
  useEffect(() => {
    const parsedDate = parseLocalDateParam(dateParam);
    if (parsedDate) {
      setSelectedDate(parsedDate);
    }
  }, [dateParam]);

  // ?view=day opens the planner in day view; anything else keeps the default.
  const initialCalendarView = useMemo<CalendarViewMode | undefined>(
    () => (searchParams.get("view") === "day" ? "day" : undefined),
    [searchParams]
  );

  // Navigate to shift planning with a specific employee selected
  const handleNavigateToEmployee = useCallback(
    (employeeId: string) => {
      setActiveTab("shifts");
      // Store employee ID so ShiftPlanningPage can pick it up
      setPendingEmployeeId(employeeId);
    },
    [setActiveTab]
  );

  // Pending employee selection — consumed by ShiftPlanningPage on mount/tab switch
  const [pendingEmployeeId, setPendingEmployeeId] = useState<string | null>(null);

  // Clear pending employee after it's been consumed
  const consumePendingEmployee = useCallback(() => {
    const id = pendingEmployeeId;
    setPendingEmployeeId(null);
    return id;
  }, [pendingEmployeeId]);

  return (
    // The planner is a wide calendar; coverage and the absence list read
    // better at the list width.
    <PageContainer
      width={activeTab === "shifts" ? "full" : "list"}
      className="flex h-full flex-col"
    >
      <PageHeader
        className="flex-none print:hidden"
        title={<Trans id="nav.shiftAbsences">Shifts & Absences</Trans>}
      />

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as TabValue)}
        className="flex-1 overflow-auto print:overflow-visible"
      >
        <ScrollableTabsList className="mb-6 print:hidden">
          <TabsTrigger value="coverage" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" aria-hidden="true" />
            <Trans id="shifts.tab.coverage">Shift Coverage</Trans>
          </TabsTrigger>
          <TabsTrigger value="shifts" className="flex items-center gap-2">
            <Clock className="h-4 w-4" aria-hidden="true" />
            <Trans id="shifts.tab.planning">Shift Planning</Trans>
          </TabsTrigger>
          <TabsTrigger value="absences" className="flex items-center gap-2">
            <UserX className="h-4 w-4" aria-hidden="true" />
            <Trans id="shifts.tab.absences">Absences</Trans>
          </TabsTrigger>
        </ScrollableTabsList>

        <TabsContent value="coverage">
          <CoverageDashboardPage
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            selectedChildcareId={selectedChildcareId}
            onChildcareChange={setSelectedChildcareId}
            selectedGroupId={selectedGroupId}
            onGroupChange={setSelectedGroupId}
            onNavigateToEmployee={handleNavigateToEmployee}
          />
        </TabsContent>

        <TabsContent value="shifts">
          <ShiftPlanningPage
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            selectedChildcareId={selectedChildcareId}
            onChildcareChange={setSelectedChildcareId}
            selectedGroupId={selectedGroupId}
            onGroupChange={setSelectedGroupId}
            consumePendingEmployee={consumePendingEmployee}
            initialCalendarView={initialCalendarView}
          />
        </TabsContent>

        <TabsContent value="absences">
          <AbsenceManagementPage />
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
